import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Layout } from '@/components/Layout';
import { NavigationProvider } from '@/contexts/NavigationContext';
import { DashboardPage } from '@/pages/DashboardPage';
import { RisksPage } from '@/pages/RisksPage';
import { AssessmentsPage } from '@/pages/AssessmentsPage';
import { ActionsPage } from '@/pages/ActionsPage';
import { ScenariosPage } from '@/pages/ScenariosPage';
import { ControlsPage } from '@/pages/ControlsPage';
import { CompliancePage } from '@/pages/CompliancePage';
import { PoliciesPage } from '@/pages/PoliciesPage';
import { SuppliersPage } from '@/pages/SuppliersPage';
import { BCPPage } from '@/pages/BCPPage';
import { AlertsPage } from '@/pages/AlertsPage';
import { ReportsPage } from '@/pages/ReportsPage';
import { AuditPage } from '@/pages/AuditPage';
import { SettingsPage } from '@/pages/SettingsPage';
// ERM Workflow Pages
import RiskAppetitePage from '@/pages/RiskAppetitePage';
import { ProcessesPage } from '@/pages/ProcessesPage';
import { KPIsPage } from '@/pages/KPIsPage';
import { NotificationsPage } from '@/pages/NotificationsPage';
import { PlaybooksPage } from '@/pages/PlaybooksPage';
import { CapacitySkillsPage } from '@/pages/CapacitySkillsPage';
import { SkillsIntelligencePage } from '@/pages/SkillsIntelligencePage';
import { BusinessForecastPage } from '@/pages/BusinessForecastPage';
import { CustomersPage } from '@/pages/CustomersPage';
import { DevToolsPage } from '@/pages/DevToolsPage';
import { IntegrationsPage } from '@/pages/IntegrationsPage';
import { AdminIntegrationsPage } from '@/pages/AdminIntegrationsPage';
import { MCPServerPage } from '@/pages/MCPServerPage';
import AIMonitoringPage from '@/pages/AIMonitoringPage';
import APIsWebhooksPage from '@/pages/APIsWebhooksPage';
import AuthenticationPage from '@/pages/AuthenticationPage';
// New Coming Soon Pages
import { ComplianceDashboard } from '@/pages/ComplianceDashboard';
import { RiskRegister } from '@/pages/RiskRegister';
import { ControlsLibrary } from '@/pages/ControlsLibrary';
import { AuditTimeline } from '@/pages/AuditTimeline';
import { MockUserProvider } from '@/hooks/useMockUser';
import './index.css';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: false,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <MockUserProvider>
        <Router>
          <NavigationProvider>
            <Layout>
            <Routes>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/risks" element={<RisksPage />} />
              <Route path="/assessments" element={<AssessmentsPage />} />
              <Route path="/actions" element={<ActionsPage />} />
              <Route path="/scenarios" element={<ScenariosPage />} />
              <Route path="/controls" element={<ControlsPage />} />
              <Route path="/compliance" element={<CompliancePage />} />
              <Route path="/policies" element={<PoliciesPage />} />
              <Route path="/suppliers" element={<SuppliersPage />} />
              <Route path="/bcp" element={<BCPPage />} />
              <Route path="/alerts" element={<AlertsPage />} />
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/audit" element={<AuditPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              {/* ERM Workflow Pages */}
              <Route path="/risk-appetite" element={<RiskAppetitePage />} />
              <Route path="/processes" element={<ProcessesPage />} />
              <Route path="/kpis" element={<KPIsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/playbooks" element={<PlaybooksPage />} />
              <Route path="/capacity-skills" element={<CapacitySkillsPage />} />
              <Route path="/capacity-skills/intelligence" element={<SkillsIntelligencePage />} />
              <Route path="/business-forecast" element={<BusinessForecastPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              {/* Hidden DevTools Page */}
              <Route path="/devtools" element={<DevToolsPage />} />
              {/* AI Integrations */}
              <Route path="/integrations" element={<IntegrationsPage />} />
              {/* Admin Integrations */}
              <Route path="/admin/integrations" element={<AdminIntegrationsPage />} />
              <Route path="/admin/integrations/mcp" element={<MCPServerPage />} />
              <Route path="/admin/integrations/ai" element={<AIMonitoringPage />} />
              <Route path="/admin/integrations/apis" element={<APIsWebhooksPage />} />
              <Route path="/admin/integrations/auth" element={<AuthenticationPage />} />
              {/* New Coming Soon Pages */}
              <Route path="/compliance-dashboard" element={<ComplianceDashboard />} />
              <Route path="/risk-register" element={<RiskRegister />} />
              <Route path="/controls-library" element={<ControlsLibrary />} />
              <Route path="/audit-timeline" element={<AuditTimeline />} />
            </Routes>
          </Layout>
          </NavigationProvider>
        </Router>
      </MockUserProvider>
    </QueryClientProvider>
  );
}

export default App;